import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { formatarMoeda } from '../../lib/formatos'
import { precosPorProduto } from '../../produtos/precosDoCatalogo'
import { fabricantesDistintos, mesmoFabricante, opcoesDeFabricante } from '../../comissoes/fabricante'
import { acaoDoCard } from '../../produtos/acaoDoCard'
import { gravarLocalEEnfileirar } from '../../sync/outbox'
import type { Produto } from '../../db/tipos'
import { confirmar } from '../../componentes/confirmar'

// A lista para aqui para nao travar o celular com um catalogo de milhares de
// itens. O aviso so aparece quando o corte REALMENTE aconteceu: pedir para
// refinar a busca de tres resultados so confunde quem esta lendo.
const LIMITE_DA_LISTA = 100

export default function ListaProdutos() {
  const [busca, setBusca] = useState('')
  const [filtroRepresentada, setFiltroRepresentada] = useState('')
  const [filtroFabricante, setFiltroFabricante] = useState('')

  const representadas = useLiveQuery(
    () => banco.representadas.filter((r) => r.apagado_em === null).sortBy('nome'),
    [],
  )
  const nomeRepresentada = (id: string) => representadas?.find((r) => r.id === id)?.nome ?? ''

  const produtos = useLiveQuery(() => {
    const termo = busca.toLowerCase()
    const base = filtroRepresentada
      ? banco.produtos.where('representada_id').equals(filtroRepresentada)
      : banco.produtos.toCollection()
    return base
      .filter(
        (p) =>
          p.apagado_em === null &&
          (termo === '' ||
            p.codigo.toLowerCase().includes(termo) ||
            p.descricao.toLowerCase().includes(termo)) &&
          // ⚠ O fabricante e filtrado AQUI, antes do `.limit`. Filtrar a lista
          // ja cortada devolveria tela vazia sempre que os 100 primeiros
          // produtos fossem de outro fabricante -- e ha catalogo de 398 itens.
          // Casa pelo MESMO criterio da regra de comissao: caixa diferente e o
          // mesmo fabricante.
          (filtroFabricante === '' || mesmoFabricante(p.fabricante, filtroFabricante)),
      )
      .limit(LIMITE_DA_LISTA)
      .toArray()
  }, [busca, filtroRepresentada, filtroFabricante])

  // Os fabricantes vem de TODOS os produtos da fornecedora escolhida, nao so
  // dos 100 que cabem na tela: filtrar por um fabricante que nao aparece na
  // primeira pagina tem de funcionar.
  const fabricantes = useLiveQuery(async () => {
    const base = filtroRepresentada
      ? banco.produtos.where('representada_id').equals(filtroRepresentada)
      : banco.produtos.toCollection()
    return fabricantesDistintos(await base.filter((p) => p.apagado_em === null).toArray())
  }, [filtroRepresentada], [])

  const opcoesFabricante = opcoesDeFabricante(fabricantes, filtroFabricante)

  // Os precos SO dos produtos que estao na tela. Carregar a tabela inteira
  // travaria o celular num catalogo de milhares de itens -- e ela usa isto na
  // rua, com o cliente esperando. O indice `produto_id` ja existe.
  const idsNaTela = (produtos ?? []).map((p) => p.id)
  const precosNaTela = useLiveQuery(
    () => banco.precos.where('produto_id').anyOf(idsNaTela).toArray(),
    [idsNaTela.join(',')],
    [],
  )
  const tabelas = useLiveQuery(() => banco.tabelas_preco.toArray(), [], [])
  const porProduto = precosPorProduto(tabelas, precosNaTela)

  // Pedido dela em 02/09: o aviso "Sem preco cadastrado" ja estava aqui, mas
  // para TIRAR o produto de circulacao era preciso abrir a ficha, rolar ate o
  // fim e desmarcar a caixinha. Agora e daqui mesmo.
  async function aoAlternarAtivo(produto: Produto) {
    const acao = acaoDoCard(produto)
    if (!(await confirmar(acao.pergunta, { botao: acao.rotulo }))) return
    // ⛔ Grava SO o `ativo`. Inativar e sobre vender daqui para a frente: nao
    // encosta em preco, em tabela nem em pedido -- pedido fechado e historia.
    // Ha guarda de contrato prendendo que nenhuma outra chave entra aqui.
    //
    // ⚠ `atualizado_em` NAO e tocado: quem carimba e o trigger do Postgres, e e
    // assim que o resto do app ja grava.
    const registroDoCard = { ...produto, ativo: acao.novoAtivo }
    await gravarLocalEEnfileirar('produtos', registroDoCard)
  }

  return (
    <section className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">Produtos</h2>
        <Link to="/produtos/novo" className="bg-teal-700 text-white rounded-lg px-3 py-2 text-sm">
          + Novo
        </Link>
      </div>
      <select
        value={filtroRepresentada}
        onChange={(e) => setFiltroRepresentada(e.target.value)}
        className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm"
      >
        <option value="">Todas as fornecedoras</option>
        {representadas?.map((r) => (
          <option key={r.id} value={r.id}>{r.nome}</option>
        ))}
      </select>
      {/* So aparece quando ha fabricante: numa conta que ainda nao usa o campo,
          um seletor de uma opcao so comeria altura de tela no celular. */}
      {opcoesFabricante.length > 0 && (
        <select
          aria-label="Filtrar por fabricante"
          value={filtroFabricante}
          onChange={(e) => setFiltroFabricante(e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm"
        >
          <option value="">Todos os fabricantes</option>
          {opcoesFabricante.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      )}
      <input
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        placeholder="Buscar por código ou descrição"
        className="w-full border border-slate-300 rounded-lg px-3 py-2"
      />
      <ul className="space-y-2">
        {produtos?.map((p) => (
          <li key={p.id} className="bg-white rounded-xl shadow-sm">
            <Link to={`/produtos/${p.id}`} className="block p-3 pb-0">
              <span className="font-medium text-slate-800">{p.descricao}</span>
              <span className="block text-xs text-slate-500">
                {p.codigo} — {nomeRepresentada(p.representada_id)}
                {p.fabricante && ` — ${p.fabricante}`}
                {!p.ativo && ' — INATIVO'}
              </span>
              {porProduto.get(p.id)?.length ? (
                <span className="block mt-2 space-y-0.5">
                  {porProduto.get(p.id)!.map((linha) => (
                    <span key={linha.tabela} className="flex justify-between gap-2 text-sm">
                      <span className="text-slate-500 truncate">{linha.tabela}</span>
                      <span className="font-medium text-slate-800 shrink-0">
                        {formatarMoeda(linha.preco)}
                      </span>
                    </span>
                  ))}
                </span>
              ) : (
                // Hoje ela so descobre isto na hora de montar o pedido, com o
                // cliente na frente. Aqui da tempo de resolver antes.
                <span className="block mt-2 text-xs text-amber-700">Sem preço cadastrado</span>
              )}
            </Link>
            {/* ⚠ FORA do <Link> de proposito: botao dentro de link navega junto,
                e a pessoa tocaria em "Inativar" e cairia na ficha, com a
                pergunta de confirmacao aparecendo por cima. Ha guarda de
                contrato prendendo a posicao. */}
            <div className="flex justify-end px-3 pb-3 pt-2">
              <button
                type="button"
                onClick={() => void aoAlternarAtivo(p)}
                className="text-sm text-slate-600 underline py-1 px-2"
              >
                {acaoDoCard(p).rotulo}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {produtos?.length === LIMITE_DA_LISTA && (
        <p className="text-xs text-slate-400">
          Mostrando os primeiros {LIMITE_DA_LISTA}. Refine a busca para achar o resto.
        </p>
      )}
    </section>
  )
}
